import React from 'react';
import { X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { MetricData, getBenchmarkStatus } from '../data/mockData';
interface MetricDetailModalProps {
  metric: MetricData;
  onClose: () => void;
}
export function MetricDetailModal({
  metric,
  onClose
}: MetricDetailModalProps) {
  const {
    details
  } = metric;
  const benchmarkStatus = getBenchmarkStatus(metric.value, metric.benchmark);
  const numValue = typeof metric.value === 'string' ? parseFloat(metric.value) : metric.value;
  const benchmarkPct = Math.round(numValue / metric.benchmark * 100);
  const statusLabel = benchmarkStatus === 'green' ? 'Above Benchmark' : benchmarkStatus === 'yellow' ? 'Near Benchmark' : 'Below Benchmark';
  const statusColor = benchmarkStatus === 'green' ? 'text-emerald-400 bg-emerald-500/10' : benchmarkStatus === 'yellow' ? 'text-amber-400 bg-amber-500/10' : 'text-red-400 bg-red-500/10';
  // Average benchmark per chart bar
  const avgBenchmarkPerBar = details.chartData.length > 0 ? metric.benchmark / details.chartData.length : 0;
  // Determine which columns to show based on the data available in the first record
  const hasDuration = details.records.some(r => r.duration);
  const hasAmount = details.records.some(r => r.amount);
  const hasRate = details.records.some(r => r.rate);
  const hasScore = details.records.some(r => r.score);
  const hasNotes = details.records.some(r => r.notes);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-184">
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-185" />

      <div className="relative bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-186">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/50" data-id="element-187">
          <div data-id="element-188">
            <h2 className="text-xl font-bold text-white" data-id="element-189">
              {metric.name} Details
            </h2>
            <div className="flex items-center gap-3 mt-1" data-id="element-190">
              <p className="text-sm text-slate-400" data-id="element-191">
                Total:{' '}
                <span className="text-white font-semibold" data-id="element-192">{metric.value}</span>
                <span className="text-slate-500 mx-1" data-id="element-193">|</span>
                Benchmark:{' '}
                <span className="text-white font-semibold" data-id="element-194">
                  {metric.benchmark}
                </span>
              </p>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-md ${statusColor}`} data-id="element-195">
                {benchmarkPct}% — {statusLabel}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-196">
            <X className="w-5 h-5" data-id="element-197" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar" data-id="element-198">
          {/* Chart Section */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5" data-id="element-199">
            <div className="flex items-center justify-between mb-4" data-id="element-200">
              <h3 className="text-sm font-medium text-slate-300" data-id="element-201">
                Volume Trend
              </h3>
              <div className="flex items-center gap-4 text-xs text-slate-400" data-id="element-202">
                <div className="flex items-center gap-1.5" data-id="element-203">
                  <div className="w-3 h-3 rounded-sm bg-sky-500" data-id="element-204"></div>
                  Actual
                </div>
                <div className="flex items-center gap-1.5" data-id="element-205">
                  <div className="w-3 h-0.5 bg-amber-400" data-id="element-206"></div>
                  Benchmark Avg
                </div>
              </div>
            </div>
            <div className="h-64 w-full" data-id="element-207">
              <ResponsiveContainer width="100%" height="100%" data-id="element-208">
                <BarChart data={details.chartData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-209">
                  <XAxis dataKey="label" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} data-id="element-210" />
                  <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `${value}`} data-id="element-211" />
                  <Tooltip cursor={{
                  fill: '#334155',
                  opacity: 0.4
                }} contentStyle={{
                  backgroundColor: '#0f172a',
                  borderColor: '#334155',
                  borderRadius: '8px',
                  color: '#f8fafc'
                }} itemStyle={{
                  color: '#38bdf8'
                }} data-id="element-212" />
                  <ReferenceLine y={avgBenchmarkPerBar} stroke="#fbbf24" strokeDasharray="6 3" strokeWidth={1.5} label={{
                  value: `Avg ${Math.round(avgBenchmarkPerBar)}`,
                  position: 'right',
                  fill: '#fbbf24',
                  fontSize: 11
                }} data-id="element-213" />
                  <Bar dataKey="value" fill="#0ea5e9" radius={[4, 4, 0, 0]} maxBarSize={40} data-id="element-214" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Records Table Section */}
          <div data-id="element-215">
            <h3 className="text-sm font-medium text-slate-300 mb-4" data-id="element-216">
              Recent Activity
            </h3>
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden" data-id="element-217">
              <div className="overflow-x-auto" data-id="element-218">
                <table className="w-full text-left border-collapse whitespace-nowrap" data-id="element-219">
                  <thead data-id="element-220">
                    <tr className="bg-slate-800 border-b border-slate-700 text-xs uppercase tracking-wider text-slate-400" data-id="element-221">
                      <th className="px-4 py-3 font-medium" data-id="element-222">Time</th>
                      <th className="px-4 py-3 font-medium" data-id="element-223">Name</th>
                      <th className="px-4 py-3 font-medium" data-id="element-224">Status</th>
                      {hasAmount && <th className="px-4 py-3 font-medium" data-id="element-225">Amount</th>}
                      {hasRate && <th className="px-4 py-3 font-medium" data-id="element-226">Rate</th>}
                      {hasScore && <th className="px-4 py-3 font-medium" data-id="element-227">Score</th>}
                      {hasDuration && <th className="px-4 py-3 font-medium" data-id="element-228">Duration</th>}
                      {hasNotes && <th className="px-4 py-3 font-medium" data-id="element-229">Notes</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50" data-id="element-230">
                    {details.records.map(record => <tr key={record.id} className="hover:bg-slate-700/30 transition-colors" data-id="element-231">
                        <td className="px-4 py-3 text-sm text-slate-300" data-id="element-232">
                          {record.time}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium" data-id="element-233">
                          <button onClick={() => {}} className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors cursor-pointer" data-id="element-234">
                            {record.name}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm" data-id="element-235">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300" data-id="element-236">
                            {record.status}
                          </span>
                        </td>
                        {hasAmount && <td className="px-4 py-3 text-sm text-slate-300" data-id="element-237">
                            {record.amount || '-'}
                          </td>}
                        {hasRate && <td className="px-4 py-3 text-sm text-slate-300" data-id="element-238">
                            {record.rate || '-'}
                          </td>}
                        {hasScore && <td className="px-4 py-3 text-sm text-slate-300" data-id="element-239">
                            {record.score || '-'}
                          </td>}
                        {hasDuration && <td className="px-4 py-3 text-sm text-slate-300" data-id="element-240">
                            {record.duration || '-'}
                          </td>}
                        {hasNotes && <td className="px-4 py-3 text-sm text-slate-400" data-id="element-241">
                            {record.notes || '-'}
                          </td>}
                      </tr>)}
                    {details.records.length === 0 && <tr data-id="element-242">
                        <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-500" data-id="element-243">
                          No recent activity found.
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-244">{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #475569; }
      `}</style>
    </div>;
}